<script setup lang="ts">
    import MobileBackground from './../../../assets/images/pta-mobile-bg.svg';
    import { gsap } from 'gsap';

    const svgRoot = ref(null);
    const parallaxGroup = ref(null);
    const circleMain = ref(null);
    const circleBackLeft = ref(null);
    const circleBackRight = ref(null);

    let onMove: (e: MouseEvent) => void;

    const mm = gsap.matchMedia();

    onMounted(() => {
        mm.add(
            {
                desktop: '(min-width: 1024px)',
                tablet: '(min-width: 768px)',
                mobile: '(max-width: 767.98px)'
            },
            (ctx) => {
                if (ctx.conditions && 'desktop' in ctx.conditions && ctx.conditions.desktop) {
                    gsap.set(circleMain.value, {
                        transformOrigin: '50% 50%',
                        xPercent: 0,
                        yPercent: 0,
                        scale: 0.9
                    });
                    gsap.set(circleBackLeft.value, {
                        transformOrigin: '50% 50%',
                        xPercent: 20,
                        yPercent: 20,
                        scale: 0.5
                    });
                } else {
                    gsap.set(circleMain.value, {
                        transformOrigin: '50% 50%',
                        xPercent: 0,
                        yPercent: 0,
                        scale: 1
                    });
                }

                const tl = gsap.timeline({ defaults: { ease: 'power2.out' } });
                tl.from(parallaxGroup.value, { opacity: 0, duration: 0.6 }).to(
                    circleMain.value,
                    {
                        scale: '+=0.03',
                        duration: 0.7,
                        yoyo: true,
                        repeat: 1
                    },
                    '>-0.1'
                );

                const layers = [
                    { el: circleBackLeft.value, damp: 0.03 },
                    { el: circleBackRight.value, damp: 0.05 },
                    { el: circleMain.value, damp: 0.07 }
                ];

                onMove = (e: MouseEvent) => {
                    const x = e.clientX - window.innerWidth / 2;
                    const y = e.clientY - window.innerHeight / 2;
                    layers.forEach(({ el, damp }, i) => {
                        gsap.to(el, {
                            x: x * damp,
                            y: y * damp,
                            duration: 0.2,
                            ease: 'none',
                            overwrite: 'auto',
                            delay: i * 0.01
                        });
                    });
                };
                window.addEventListener('mousemove', onMove, { passive: true });

                return () => {
                    window.removeEventListener('mousemove', onMove);
                };
            }
        );
    });

    onBeforeUnmount(() => {
        mm.revert();
    });
</script>

<template>
    <div class="background animated">
        <div class="white-gradient"></div>

        <img
            :src="MobileBackground"
            class="mobile"
            alt=""
        />

        <svg
            class="bg-stretch desktop"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 3940 2160"
            preserveAspectRatio="none"
            aria-hidden="true"
        >
            <defs>
                <linearGradient id="bg-b">
                    <stop
                        offset="0"
                        style="stop-color: #027f95"
                    />
                    <stop
                        offset="1"
                        style="stop-color: #f39324"
                    />
                </linearGradient>
                <linearGradient
                    id="bg-e"
                    href="#bg-b"
                    x1="2232.353"
                    x2="2227.769"
                    y1="-50.423"
                    y2="2209.434"
                    gradientTransform="matrix(1 0 0 .88085 5.818 102.538)"
                    gradientUnits="userSpaceOnUse"
                />
            </defs>

            <rect
                class="background-gradient"
                x="0"
                y="0"
                width="100%"
                height="100%"
                fill="url(#bg-e)"
            />
        </svg>

        <svg
            ref="svgRoot"
            class="bg-svg desktop"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 3940 2160"
            preserveAspectRatio="xMidYMid slice"
            aria-hidden="true"
        >
            <defs>
                <linearGradient id="d">
                    <stop
                        offset="0"
                        style="stop-color: #fff; stop-opacity: 0.166"
                    />
                    <stop
                        offset="1"
                        style="stop-color: #fff; stop-opacity: 0"
                    />
                </linearGradient>
                <linearGradient id="c">
                    <stop
                        offset="0"
                        style="stop-color: #fff; stop-opacity: 0.0394"
                    />
                    <stop
                        offset="1"
                        style="stop-color: #fff; stop-opacity: 0"
                    />
                </linearGradient>
                <linearGradient id="b">
                    <stop
                        offset="0"
                        style="stop-color: #027f95"
                    />
                    <stop
                        offset="1"
                        style="stop-color: #f39324"
                    />
                </linearGradient>
                <linearGradient id="a">
                    <stop
                        offset="0"
                        style="stop-color: #b70f80"
                    />
                    <stop
                        offset="1"
                        style="stop-color: #b70380; stop-opacity: 0.08"
                    />
                </linearGradient>

                <linearGradient
                    id="f"
                    href="#a"
                    x1="1586.025"
                    x2="1599.247"
                    y1="-197.107"
                    y2="3450.421"
                    gradientTransform="translate(-233.074 -3.702)"
                    gradientUnits="userSpaceOnUse"
                />
                <linearGradient
                    id="e"
                    href="#b"
                    x1="2232.353"
                    x2="2227.769"
                    y1="-50.423"
                    y2="2209.434"
                    gradientTransform="matrix(1 0 0 .88085 5.818 102.538)"
                    gradientUnits="userSpaceOnUse"
                />
                <linearGradient
                    id="g"
                    href="#c"
                    x1="11.666"
                    x2="-34.998"
                    y1="-1184.089"
                    y2="1907.376"
                    gradientTransform="matrix(1.34219 0 0 1.43586 3189.798 2083.223)"
                    gradientUnits="userSpaceOnUse"
                />
                <linearGradient
                    id="h"
                    href="#d"
                    x1="11.666"
                    x2="-34.998"
                    y1="-1184.089"
                    y2="1907.376"
                    gradientTransform="matrix(1.34219 0 0 1.43586 -251.677 -217.13)"
                    gradientUnits="userSpaceOnUse"
                />
            </defs>

            <g
                id="scene"
                ref="parallaxGroup"
            >
                <ellipse
                    ref="circleMain"
                    class="circle-main"
                    cx="1327.74"
                    cy="1110.182"
                    rx="1244.525"
                    ry="1260.569"
                    style="fill: url(#f)"
                />
                <ellipse
                    ref="circleBackRight"
                    class="circle-secondary"
                    cx="3252.429"
                    cy="2154.413"
                    rx="1518.813"
                    ry="1587.119"
                    style="fill: url(#g)"
                />
                <ellipse
                    ref="circleBackLeft"
                    class="circle-tertiary"
                    cx="-189.045"
                    cy="-145.939"
                    rx="1518.813"
                    ry="1587.119"
                    style="fill: url(#h)"
                />
            </g>
        </svg>
    </div>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/pta-family/pta-hero-background-animated.scss';
</style>
